/**
 * The capacity routes' typed edge (platform design §5.1.1; `api/src/socialsrc_api/schemas/
 * capacity.py`, `routers/capacity.py`, CA-3 — not yet landed). Written here, field for field
 * from CA-3's pydantic models, so this task is not blocked on the backend: CA-8's end-to-end run
 * is what proves the two agree.
 */

import { z } from 'zod';

import { itemsOf } from '../../api/schemas';

export const capacitySchema = z.object({
  campaign_slots: z.number().int().nullable(),
  campaigns_in_use: z.number().int(),
  slots_available: z.number().int().nullable(),
});
export type Capacity = z.infer<typeof capacitySchema>;

export const capacityRequestSchema = z.object({
  id: z.uuid(),
  kind: z.enum(['CREDITS', 'SLOTS']),
  credits_requested: z.number().int().nullable(),
  slots_requested: z.number().int().nullable(),
  accounts_to_track: z.number().int().nullable(),
  context: z.string(),
  state: z.enum(['OPEN', 'IN_REVIEW', 'GRANTED', 'DECLINED', 'WITHDRAWN']),
  created_at: z.iso.datetime({ offset: true }),
  decided_at: z.iso.datetime({ offset: true }).nullable(),
  decision_note: z.string().nullable(),
});
export type CapacityRequest = z.infer<typeof capacityRequestSchema>;

export const capacityRequestsResponseSchema = itemsOf(capacityRequestSchema);

/** `ConsumptionOut` (`api/src/socialsrc_api/schemas/commercial.py`): a trailing 7-day window,
 *  oldest first, one entry per UTC calendar day — always 7, a day with no spend is a computed
 *  `0`, never omitted. `by_campaign` lists only a campaign with a debit in the window, busiest
 *  first; a tenant with no capacity-attributed spend at all still gets a real, empty list, not an
 *  absent one. */
export const dailyConsumptionSchema = z.object({
  date: z.iso.date(),
  credits: z.number().int(),
});
export type DailyConsumption = z.infer<typeof dailyConsumptionSchema>;

export const campaignConsumptionSchema = z.object({
  campaign_id: z.uuid(),
  campaign_name: z.string(),
  credits: z.number().int(),
});
export type CampaignConsumption = z.infer<typeof campaignConsumptionSchema>;

export const consumptionSchema = z.object({
  unit: z.literal('tenant_credits'),
  daily: z.array(dailyConsumptionSchema),
  by_campaign: z.array(campaignConsumptionSchema),
});
export type Consumption = z.infer<typeof consumptionSchema>;

/** `CapacityRequestIn` (CA-3's Interfaces block). No zod schema: `ApiClient.postParsed` only
 *  parses the response, never the outgoing body, matching every other mutation in this codebase
 *  (`useRenameTag`, `useDeleteTag`). `kind` decides which amount field the server requires —
 *  checked server-side, not here. */
export interface CapacityRequestInput {
  kind: 'CREDITS' | 'SLOTS';
  credits_requested: number | null;
  slots_requested: number | null;
  accounts_to_track: number | null;
  context: string;
}
